import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';

import Layout from './components/Layout';
import Home from './pages/Home';
import { useSmoothScroll } from './hooks/useSmoothScroll';

// Long-form pages are split out: the markdown content (6 locales for
// Terms/Privacy) dwarfs the entry bundle. Home stays eager so the
// first paint is fast.
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Faq = lazy(() => import('./pages/Faq'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Download = lazy(() => import('./pages/Download'));
// Invite is a focused single-action landing — it lives outside the
// `Layout` route so the Header / Footer don't compete with the
// "copy code + install app" call to action.
const Invite = lazy(() => import('./pages/Invite'));

export default function App() {
  useSmoothScroll();

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route
          path="terms"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Terms />
            </Suspense>
          }
        />
        <Route
          path="privacy"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Privacy />
            </Suspense>
          }
        />
        <Route
          path="faq"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Faq />
            </Suspense>
          }
        />
        <Route
          path="download"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Download />
            </Suspense>
          }
        />
        <Route
          path="*"
          element={
            <Suspense fallback={<RouteFallback />}>
              <NotFound />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="i/:code"
        element={
          <Suspense fallback={<RouteFallback />}>
            <Invite />
          </Suspense>
        }
      />
    </Routes>
  );
}

/**
 * Empty placeholder for the suspense boundary. Long-form pages load
 * fast enough on a normal connection that the flash of a spinner
 * would itself feel like a glitch — leave the layout empty and let
 * the page pop in.
 */
function RouteFallback() {
  return <div className="min-h-[60vh]" aria-hidden />;
}
